import { Filter, FilterInput, FilterConfigSlider, FilterConfigStarRating, FilterConfig, FilterConfigToggle } from 'types/Search';

export type State = {
  // Maintaining this array is an optimization for checking if updates have occurred.
  saved: Filter[];
  savedByKey: { [key: string]: Filter };
  changes: { [key: string]: Filter };
};

export enum Types {
  CHANGE_ONE,
  CLEAR_CHANGES,
  UPDATE_ALL
}

type Action =
  | {
      type: Types.CHANGE_ONE;
      payload: { key: string; changes: FilterConfig };
    }
  | {
      type: Types.CLEAR_CHANGES;
    }
  | {
      type: Types.UPDATE_ALL;
      payload: Filter[];
    };

export const initialState: State = { saved: [], savedByKey: {}, changes: {} };

const generateFilterChangeset = (filter: Filter, configUpdates: FilterConfig): Filter => {
  switch (filter.type) {
    case FilterInput.CHECKBOX:
    case FilterInput.RADIO:
      return {
        ...filter,
        config: {
          ...filter.config,
          ...(configUpdates as FilterConfigToggle)
        }
      };
    case FilterInput.SLIDER:
      return {
        ...filter,
        config: {
          ...filter.config,
          ...(configUpdates as FilterConfigSlider)
        }
      };
    case FilterInput.STAR_RATING:
      return {
        ...filter,
        config: configUpdates as FilterConfigStarRating
      };
    default:
      return filter;
  }
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case Types.CLEAR_CHANGES:
      return {
        ...state,
        changes: {}
      };
    case Types.UPDATE_ALL:
      // Filter objects are updated upstream on change and strict equality comparison
      // will always show these as a change. Add some logic here to prevent side-effects
      // and re-renders for anyone using the reducer.
      if (JSON.stringify(action.payload) === JSON.stringify(state.saved)) {
        return state;
      }

      return {
        saved: action.payload,
        savedByKey: action.payload.reduce(
          (memo, filter) => ({
            ...memo,
            [filter.key]: filter
          }),
          {}
        ),
        changes: {}
      };
    case Types.CHANGE_ONE:
      return {
        ...state,
        changes: {
          ...state.changes,
          [action.payload.key]: generateFilterChangeset(state.savedByKey[action.payload.key], action.payload.changes)
        }
      };
    default:
      return state;
  }
};

export default reducer;
